/**
 * WELLE D — Zentrale Cache-TTL-Konstanten.
 *
 * Eine Quelle für alle TTL-Definitionen damit nicht in 5 verschiedenen Files
 * unterschiedliche "frisch"-Grenzen verwendet werden.
 *
 * Regel:
 *   FRESH_MS  → bis dahin ist Cache "frisch", kein Live-Call nötig
 *   STALE_MAX_MS → ab dann ist Cache zu alt um noch sinnvoll zu sein (UI rendert "fehlt")
 *
 * Zwischen FRESH und STALE_MAX nennen wir es "stale-acceptable" — UI rendert
 * Badge "Daten X Std alt", Score-Engine darf es noch nutzen.
 */

export const QUOTE_FRESH_MS = 60 * 60 * 1000; // 1 Std
export const QUOTE_STALE_MAX_MS = 48 * 60 * 60 * 1000; // 48 Std — älter = "fehlt"

export const OHLCV_FRESH_MS = 8 * 60 * 60 * 1000; // 8 Std
export const OHLCV_STALE_MAX_MS = 7 * 24 * 60 * 60 * 1000; // 1 Woche — Wochenend-Aushalt

export const FH_NEWS_FRESH_MS = 24 * 60 * 60 * 1000; // 24 Std — Finnhub-News daily refreshed
export const FH_NEWS_STALE_MAX_MS = 7 * 24 * 60 * 60 * 1000; // 1 Woche

export const AI_ENRICH_FRESH_MS = 7 * 24 * 60 * 60 * 1000; // 1 Woche
export const AI_ENRICH_STALE_MAX_MS = 60 * 24 * 60 * 60 * 1000; // 60 Tage

export const AI_PRICE_TARGET_FRESH_MS = 14 * 24 * 60 * 60 * 1000; // 14 Tage
export const AI_PRICE_TARGET_STALE_MAX_MS = 60 * 24 * 60 * 60 * 1000;

/**
 * Hilfsfunktion: ist ein Cache-Eintrag "frisch"?
 */
export function isFresh(fetchedAt: string | null | undefined, freshMs: number): boolean {
  if (!fetchedAt) return false;
  const age = Date.now() - new Date(fetchedAt).getTime();
  return age >= 0 && age <= freshMs;
}

/**
 * Hilfsfunktion: ist ein Cache-Eintrag "noch akzeptabel" (zwischen fresh und stale_max)?
 */
export function isStaleAcceptable(
  fetchedAt: string | null | undefined,
  freshMs: number,
  staleMaxMs: number
): boolean {
  if (!fetchedAt) return false;
  const age = Date.now() - new Date(fetchedAt).getTime();
  return age > freshMs && age <= staleMaxMs;
}

/**
 * Hilfsfunktion: Cache-Eintrag ist zu alt — UI sollte "fehlt"-Stand zeigen.
 */
export function isTooStale(
  fetchedAt: string | null | undefined,
  staleMaxMs: number
): boolean {
  if (!fetchedAt) return true;
  const age = Date.now() - new Date(fetchedAt).getTime();
  return age > staleMaxMs;
}

/**
 * Liefert das Alter eines Cache-Eintrags als menschlich lesbaren String.
 */
export function formatAge(fetchedAt: string | null | undefined): string | null {
  if (!fetchedAt) return null;
  const ageMs = Date.now() - new Date(fetchedAt).getTime();
  if (ageMs < 0) return 'frisch';
  const minutes = Math.floor(ageMs / 60000);
  if (minutes < 1) return 'gerade eben';
  if (minutes < 60) return `${minutes} Min alt`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} Std alt`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} T. alt`;
  return `${Math.floor(days / 7)} W. alt`;
}
